var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useCallback, useState, useMemo, } from "react";
import Welcome from "../../../../components/Welcome";
import IconWelcome from "../../../../components/icons/IconWelcome";
import IconAmbassadors from "../../../../components/icons/IconAmbassadors";
import IconPlant from "../../../../components/icons/IconPlant";
import IconSettings from "../../../../components/icons/IconSettings";
import { useAmbassadors, useAmbassadorsRefresh, } from "../../../../hooks/useAmbassadors";
import { classes } from "../../../../utils/classes";
import { visibleUnderCursor } from "../../../../utils/dom";
import useChatCommand from "../../../../hooks/useChatCommand";
import useSettings from "../../hooks/useSettings";
import useSleeping from "../../hooks/useSleeping";
import AmbassadorsOverlay from "./Ambassadors";
import SettingsOverlay from "./Settings";
import Buttons, {} from "../Buttons";
// Show command-triggered popups for 10s
var commandTimeout = 10000;
var overlayOptions = [
    {
        key: "welcome",
        type: "primary",
        icon: IconWelcome,
        title: "Welcome to Alveus",
        component: function (props) { return (_jsx(Welcome, __assign({}, props, { className: classes("absolute top-0 left-0 mx-4 my-6", props.className) }))); },
    },
    {
        key: "ambassadors",
        type: "primary",
        icon: IconAmbassadors,
        title: "Explore our Animal Ambassadors",
        component: AmbassadorsOverlay,
        condition: function (_a) {
            var ambassadors = _a.ambassadors;
            return Object.values(ambassadors !== null && ambassadors !== void 0 ? ambassadors : {}).some(function (a) { return a.species.class.key !== "plantae"; });
        },
    },
    {
        key: "ambassadorPlants",
        type: "primary",
        icon: IconPlant,
        title: "Explore our Plant Ambassadors",
        component: function (props) { return _jsx(AmbassadorsOverlay, __assign({}, props, { plants: true })); },
        condition: function (_a) {
            var ambassadors = _a.ambassadors;
            return Object.values(ambassadors !== null && ambassadors !== void 0 ? ambassadors : {}).some(function (a) { return a.species.class.key === "plantae"; });
        },
    },
    {
        key: "settings",
        type: "secondary",
        icon: IconSettings,
        title: "Extension Settings",
        component: SettingsOverlay,
    },
];
export var isValidOverlayKey = function (key) {
    return key === "" || overlayOptions.some(function (option) { return option.key === key; });
};
var hiddenClass = "invisible opacity-0 -translate-x-10 motion-reduce:translate-x-0";
export default function Overlay() {
    var settings = useSettings();
    var _a = useSleeping(), sleeping = _a.sleeping, wake = _a.wake, addSleepListener = _a.on, removeSleepListener = _a.off;
    var ambassadors = useAmbassadors();
    var refresh = useAmbassadorsRefresh();
    var options = useMemo(function () {
        return overlayOptions.filter(function (option) {
            return !option.condition || option.condition({ ambassadors: ambassadors });
        });
    }, [ambassadors]);
    var _b = useState({}), activeAmbassador = _b[0], setActiveAmbassador = _b[1];
    var _c = useState(settings.openedMenu.value), visibleOption = _c[0], setVisibleOption = _c[1];
    var timeoutRef = useRef(null);
    var awakingRef = useRef(false);
    // update setting when opened menu changes
    useEffect(function () {
        settings.openedMenu.change(visibleOption);
    }, [visibleOption]);
    // open saved (or default) menu when mounted
    useEffect(function () {
        setVisibleOption(settings.openedMenu.value);
    }, [settings.openedMenu.value]);
    // When a chat command is run, wake the overlay
    useChatCommand(useCallback(function (command) {
        if (command === "refresh") {
            setTimeout(function () {
                refresh === null || refresh === void 0 ? void 0 : refresh();
            }, Math.floor(Math.random() * 120 * 1000));
            return;
        }
        if (!settings.disableChatPopup.value) {
            var ambassador = ambassadors === null || ambassadors === void 0 ? void 0 : ambassadors[command];
            if (ambassador)
                setActiveAmbassador({ key: command, isCommand: true });
            else if (command !== "welcome")
                return;
            // Show the card
            setVisibleOption(ambassador
                ? ambassador.species.class.key === "plantae"
                    ? "ambassadorPlants"
                    : "ambassadors"
                : "welcome");
            // Dismiss the overlay after a delay
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(function () {
                setVisibleOption("");
                setActiveAmbassador({});
            }, commandTimeout);
            // Track that we're waking up, so that we don't immediately clear the timeout, and wake the overlay
            awakingRef.current = true;
            wake(commandTimeout);
        }
    }, [refresh, settings.disableChatPopup.value, ambassadors, wake]));
    // Ensure we clean up the timer when we unmount
    useEffect(function () { return function () {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
    }; }, []);
    // If the user interacts with the overlay, clear the auto-dismiss timer
    // Except if we just triggered this wake, in which case we want to ignore it
    useEffect(function () {
        var callback = function () {
            if (awakingRef.current)
                awakingRef.current = false;
            else if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
        };
        addSleepListener("wake", callback);
        return function () { return removeSleepListener("wake", callback); };
    }, [addSleepListener, removeSleepListener]);
    // Handle body clicks, dismissing the overlay if the user clicks outside of it
    var bodyClick = useCallback(function (e) {
        if (!visibleUnderCursor(e)) {
            setVisibleOption("");
        }
    }, []);
    // If the user clicks anywhere in the body, except the overlay itself, close the panels
    // Bind it during the capture phase so that we can process it before any other click handlers
    useEffect(function () {
        document.body.addEventListener("click", bodyClick, true);
        return function () { return document.body.removeEventListener("click", bodyClick, true); };
    }, [bodyClick]);
    // Handle body double clicks, ignoring them inside of overlay elements
    var bodyDblClick = useCallback(function (e) {
        if (visibleUnderCursor(e)) {
            e.stopPropagation();
        }
    }, []);
    // If the user double clicks anywhere in the overlay itself, stop propagating the event
    // This stops double clicks from toggling fullscreen video which is the default behavior
    useEffect(function () {
        document.body.addEventListener("dblclick", bodyDblClick, true);
        return function () {
            return document.body.removeEventListener("dblclick", bodyDblClick, true);
        };
    }, [bodyDblClick]);
    // Generate the context for the overlay options
    var context = useMemo(function () { return ({
        activeAmbassador: activeAmbassador,
        setActiveAmbassador: setActiveAmbassador,
    }); }, [activeAmbassador]);
    // Unselect ambassador if ambassador is no longer available after refresh
    useEffect(function () {
        if (ambassadors &&
            activeAmbassador.key &&
            !(ambassadors === null || ambassadors === void 0 ? void 0 : ambassadors[activeAmbassador.key])) {
            setActiveAmbassador({});
        }
    }, [ambassadors, activeAmbassador.key]);
    return (_jsxs("div", { className: classes("flex h-full w-full transition-[opacity,visibility,transform,translate] will-change-[opacity,transform,translate]", sleeping &&
            !(process.env.NODE_ENV === "development" &&
                settings.disableOverlayHiding.value) &&
            hiddenClass), children: [_jsx(Buttons, { options: options, onClick: setVisibleOption, active: visibleOption }), _jsx("div", { className: "relative h-full w-full", children: options.map(function (option) { return (_jsx(option.component, { context: context, className: classes("transition-[opacity,visibility,transform,translate] will-change-[opacity,transform,translate]", visibleOption !== option.key && hiddenClass) }, option.key)); }) })] }));
}
